import axios from "axios";

const BASE = "/api/portal";
const TOKEN_KEY = "portal_token";
const REFRESH_KEY = "portal_refresh_token";
const CLIENT_KEYS = ["portal_client:v1", "portal_client"];

function portalStorage() {
  return sessionStorage.getItem(REFRESH_KEY) || sessionStorage.getItem(TOKEN_KEY)
    ? sessionStorage
    : localStorage;
}

export function getStoredPortalToken() {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || "";
}

export function getStoredPortalClient() {
  for (const storage of [sessionStorage, localStorage]) {
    for (const key of CLIENT_KEYS) {
      const raw = storage.getItem(key);
      if (!raw) continue;
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
  }
  return {};
}

export function savePortalSession({ token, refreshToken, client, persistent }) {
  const storage = persistent ? localStorage : sessionStorage;
  const other = persistent ? sessionStorage : localStorage;
  other.removeItem(TOKEN_KEY);
  other.removeItem(REFRESH_KEY);
  CLIENT_KEYS.forEach((key) => other.removeItem(key));
  storage.setItem(TOKEN_KEY, token);
  if (refreshToken) storage.setItem(REFRESH_KEY, refreshToken);
  if (client) storage.setItem("portal_client:v1", JSON.stringify(client));
}

export function saveStoredPortalClient(client) {
  portalStorage().setItem("portal_client:v1", JSON.stringify(client));
}

export function clearPortalAuthStorage() {
  for (const storage of [sessionStorage, localStorage]) {
    storage.removeItem(TOKEN_KEY);
    storage.removeItem(REFRESH_KEY);
    CLIENT_KEYS.forEach((key) => storage.removeItem(key));
  }
}

export function createPortalApi(token, onTokenRefreshed, onTokenExpired) {
  const instance = axios.create({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const original = err.config;
      const storage = portalStorage();
      const refreshToken = storage.getItem(REFRESH_KEY);
      if (err.response?.status === 401 && !original._retry && refreshToken) {
        original._retry = true;
        try {
          const res = await axios.post(`${BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const newToken = res.data.access_token;
          const newRefresh = res.data.refresh_token || refreshToken;
          storage.setItem(TOKEN_KEY, newToken);
          storage.setItem(REFRESH_KEY, newRefresh);
          original.headers = original.headers || {};
          original.headers["Authorization"] = `Bearer ${newToken}`;
          if (onTokenRefreshed) onTokenRefreshed(newToken);
          return instance(original);
        } catch {
          clearPortalAuthStorage();
          if (onTokenExpired) onTokenExpired();
        }
      }
      return Promise.reject(err);
    },
  );

  return instance;
}

export const portalLogin = (slug, password) =>
  axios.post(`${BASE}/login`, { slug, password });

export const fetchDocuments = (api) => api.get(`${BASE}/documents`);
export const fetchSubclients = (api) => api.get(`${BASE}/subclients`);
export const fetchChatbotUsers = (api) => api.get(`${BASE}/chatbot-users`);

export const fetchPortalQuotas = (api) => api.get(`${BASE}/quotas`);

export const uploadFiles = (api, formData, subclientId) => {
  const url = subclientId
    ? `${BASE}/upload?subclient_id=${subclientId}`
    : `${BASE}/upload`;
  return api.post(url, formData, { timeout: 600000 });
};

export const initChunkedUpload = (api, data, subclientId) => {
  const url = subclientId
    ? `${BASE}/upload/init?subclient_id=${subclientId}`
    : `${BASE}/upload/init`;
  return api.post(url, data, { timeout: 60000 });
};

export const uploadChunk = (api, formData) =>
  api.post(`${BASE}/upload/chunk`, formData, { timeout: 600000 });

export const completeChunkedUpload = (api, data) =>
  api.post(`${BASE}/upload/complete`, data, { timeout: 600000 });

/** Demande une synchronisation côté agent local (dossier RAGUIA). */
export const requestDocumentSync = (api) =>
  api.post(`${BASE}/documents/sync-request`);

/** Annule une demande de sync en attente (aucun agent, clic par erreur, etc.). */
export const cancelDocumentSync = (api) =>
  api.post(`${BASE}/documents/sync-request/cancel`);

/** Infos MAJ agent (version recommandée par le serveur — lecture seule). */
export const fetchLocalAgentUpdateInfo = (api) =>
  api.get(`${BASE}/local-agent/update-info`);

export const deleteDocument = (api, docId) =>
  api.delete(`${BASE}/documents/${docId}`);
export const bulkDeleteDocuments = (api, docIds) =>
  api.post(`${BASE}/documents/bulk-delete`, { document_ids: docIds });
export const assignDocument = (api, docId, data) =>
  api.post(`${BASE}/documents/${docId}/assign`, data);

export const createChatbotUser = (api, data) =>
  api.post(`${BASE}/chatbot-users`, data);
export const deleteChatbotUser = (api, userId) =>
  api.delete(`${BASE}/chatbot-users/${userId}`);
export const updateChatbotUserPassword = (api, userId, password) =>
  api.patch(`${BASE}/chatbot-users/${userId}/password`, { password });
export const updateChatbotUser = (api, userId, data) =>
  api.patch(`${BASE}/chatbot-users/${userId}`, data);

/** Répartition égale des 100 % entre tous les accès (remplace les % existants). */
export const rebalanceMessagePercentsEqually = (api) =>
  api.post(`${BASE}/chatbot-users/rebalance-message-percents`);

/** Export RGPD JSON (téléchargement navigateur). */
export async function downloadGdprDataExport(api, userId) {
  try {
    const res = await api.get(
      `${BASE}/chatbot-users/${userId}/gdpr-data-export`,
      { responseType: "blob" },
    );
    const blob =
      res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = `raguia-export-accès-${userId}.json`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        err.response.data = parsed;
      } catch {
        err.response.data = {
          detail: "Réponse inattendue du serveur lors de l'export.",
        };
      }
    }
    throw err;
  }
}

export const eraseChatbotUserConversations = (api, userId) =>
  api.post(`${BASE}/chatbot-users/${userId}/erase-conversations`);

export const changePassword = (api, password) =>
  api.patch(`${BASE}/password`, { password });
